import { ActivatedRouteSnapshot, RouterStateSnapshot, Routes } from '@angular/router';
import { HomePageComponent } from './pages/home-page/home-page.component';
import { AuthGuard, redirectLoggedInTo, redirectUnauthorizedTo } from '@angular/fire/auth-guard';
import { MainLayoutComponent } from './layouts/main-layout/main-layout.component';

const redirectUnauthorizedToLogin = (next: ActivatedRouteSnapshot, state: RouterStateSnapshot) =>
  redirectUnauthorizedTo(`/login?redirectTo=${state.url}`);
const redirectLoggedInToPreviousPage = (next: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  let redirectUrl = '/';
  try {
    const redirectToUrl = new URL(state.url, location.origin);
    const params = new URLSearchParams(redirectToUrl.search);
    redirectUrl = params.get('redirectTo') || '/';
  } catch { }
  return redirectLoggedInTo(redirectUrl);
};

export const routes: Routes = [
  {
    path: '',
    component: MainLayoutComponent,
    children: [
      { path: '', component: HomePageComponent, data: { fullWidth: true, hideBanner: true } },
      { path: 'restaurants', loadComponent: () => import('./pages/restaurants/restaurants.component').then(m => m.RestaurantsComponent), data: { title: 'Restaurants & Takeaways', fullWidth: true } },
      { path: 'terms', loadComponent: () => import('./pages/terms/terms.component').then(m => m.TermsComponent), data: { title: 'Terms, Conditions & Privacy Policy' } },
      { path: 'contact', loadComponent: () => import('./pages/contact-us/contact-us.component').then(m => m.ContactUsComponent), data: { title: 'Contact Us' } },
      { path: 'about', loadComponent: () => import('./pages/about/about.component').then(m => m.AboutComponent) },
      {
        path: 'login', loadComponent: () => import('briteatlib').then(m => m.LoginComponent),
        canActivate: [AuthGuard], data: { authGuardPipe: redirectLoggedInToPreviousPage }
      },
      { path: 'favorite', loadComponent: () => import('./components/favorite/favorite-list/favorite-list.component').then(m => m.FavoriteListComponent) },
      { path: 'book-a-table', loadComponent: () => import('./pages/book-a-table/book-a-table.component').then(m => m.BookATableComponent), data: { title: 'Book a Table' } },
    ],
  },
  {
    path: '',
    loadComponent: () => import('./layouts/profile-layout/profile-layout.component').then(m => m.ProfileLayoutComponent),
    children: [
      { path: 'profile', loadComponent: () => import('./components/general/user-profile/user-profile.component').then(m => m.UserProfileComponent) },
      { path: 'rewards', loadComponent: () => import('./components/loyalty/reward-list/reward-list.component').then(m => m.RewardListComponent) },
      { path: 'order', loadComponent: () => import('./components/user/order-list/order-list.component').then(m => m.OrderListComponent) },
      { path: 'order/:orderId', loadComponent: () => import('./components/user/user-order-detail/user-order-detail.component').then(m => m.UserOrderDetailComponent) },
      { path: 'order/current', loadComponent: () => import('./components/order/current-orders/current-orders.component').then(m => m.CurrentOrdersComponent) },
    ],
    canActivate: [AuthGuard], data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: '',
    loadComponent: () => import('./layouts/simple-layout/simple-layout.component').then(m => m.SimpleLayoutComponent),
    children: [
      { path: 'checkout', loadComponent: () => import('./components/order/checkout/checkout.component').then(m => m.CheckoutComponent) },
      { path: 'payment', loadComponent: () => import('./components/order/payment/payment.component').then(m => m.PaymentComponent) },
      { path: 'payment/:pid', loadComponent: () => import('./components/order/payment-result/payment-result.component').then(m => m.PaymentResultComponent) },
    ]
  },
  {
    path: 'britlink', loadComponent: () => import('./layouts/britlink-layout/britlink-layout.component').then(m => m.BritlinkLayoutComponent),
  },
  // { path: 'britlink', loadComponent: () => import('./components/panel/order-panel/order-panel.component').then(m => m.OrderPanelComponent) },
  {
    path: 'panel',
    loadComponent: () => import('./layouts/panel-layout/panel-layout.component').then(m => m.PanelLayoutComponent),
    data: { title: 'Business Panel' },
    children: [
      { path: '', loadComponent: () => import('./components/panel/business-panel/business-panel.component').then(m => m.BusinessPanelComponent) },
      { path: 'availability', loadComponent: () => import('./components/panel/menu-panel/menu-panel.component').then(m => m.MenuPanelComponent) },
      { path: 'order', loadComponent: () => import('./components/panel/order-panel/order-panel.component').then(m => m.OrderPanelComponent) },
      { path: 'feedback', loadComponent: () => import('./components/panel/feedback-panel/feedback-panel.component').then(m => m.FeedbackPanelComponent) },
      { path: 'reservation', loadComponent: () => import('./components/panel/reservation-panel/reservation-panel.component').then(m => m.ReservationPanelComponent) },
      { path: 'payments', loadComponent: () => import('briteatlib').then(m => m.ManagePaymentsComponent) },
      {
        path: 'menu', data: { basePath: '/panel' }, children: [
          { path: ':bid', loadComponent: () => import('briteatlib').then(m => m.MenuListComponent) },
          { path: ':bid/price-wizard', loadComponent: () => import('briteatlib').then(m => m.PriceWizardComponent) },
          { path: ':bid/add', loadComponent: () => import('briteatlib').then(m => m.MenuSaveComponent) },
          { path: ':bid/option', loadComponent: () => import('briteatlib').then(m => m.MenuOptionListComponent) },
          { path: ':bid/option/add', loadComponent: () => import('briteatlib').then(m => m.MenuOptionSaveComponent) },
          { path: ':bid/option/:id', loadComponent: () => import('briteatlib').then(m => m.MenuOptionSaveComponent) },
          { path: ':bid/item', loadComponent: () => import('briteatlib').then(m => m.MenuItemListComponent) },
          { path: ':bid/item/add', loadComponent: () => import('briteatlib').then(m => m.MenuItemSaveComponent) },
          { path: ':bid/item/:id', loadComponent: () => import('briteatlib').then(m => m.MenuItemSaveComponent) },
          { path: ':bid/:mid', loadComponent: () => import('briteatlib').then(m => m.MenuSaveComponent) },
          { path: ':bid/:mid/item', loadComponent: () => import('briteatlib').then(m => m.MenuItemListComponent) },
          { path: ':bid/:mid/add', loadComponent: () => import('briteatlib').then(m => m.MenuItemSaveComponent) },
          { path: ':bid/:mid/:id', loadComponent: () => import('briteatlib').then(m => m.MenuItemSaveComponent) },
        ]
      },
      {
        path: 'promotion', data: { basePath: '/panel' }, children: [
          { path: '', loadComponent: () => import('briteatlib').then(m => m.PromotionListComponent) },
          { path: ':bid', loadComponent: () => import('briteatlib').then(m => m.PromotionListComponent) },
          { path: ':bid/add', loadComponent: () => import('briteatlib').then(m => m.PromotionSaveComponent) },
          { path: ':bid/:id', loadComponent: () => import('briteatlib').then(m => m.PromotionSaveComponent) },
        ]
      },
      {
        path: 'reward', data: { basePath: '/panel' }, children: [
          { path: '', loadComponent: () => import('briteatlib').then(m => m.RewardListComponent) },
          { path: ':bid', loadComponent: () => import('briteatlib').then(m => m.RewardListComponent) },
          { path: ':bid/add', loadComponent: () => import('briteatlib').then(m => m.RewardSaveComponent) },
          { path: ':bid/:id', loadComponent: () => import('briteatlib').then(m => m.RewardSaveComponent) },
        ]
      },
      {
        path: 'report', data: { basePath: '/panel' }, children: [
          { path: ':bid', loadComponent: () => import('briteatlib').then(m => m.OrderReportComponent) },
        ]
      },
      {
        path: 'transact', data: { basePath: '/panel' }, children: [
          { path: ':bid', loadComponent: () => import('briteatlib').then(m => m.TransactListComponent) },
          { path: ':bid/current', loadComponent: () => import('briteatlib').then(m => m.TransactDetailsComponent) },
          { path: ':bid/:tid', loadComponent: () => import('briteatlib').then(m => m.TransactDetailsComponent) },
        ]
      },
      { path: ':id', loadComponent: () => import('briteatlib').then(m => m.BusinessSaveComponent) }
    ]
  },
  {
    path: '',
    loadComponent: () => import('./layouts/empty-layout/empty-layout.component').then(m => m.EmptyLayoutComponent),
    children: [
      { path: 'auth', loadComponent: () => import('./components/auth/auth/auth.component').then(m => m.AuthComponent) },
    ]
  }
];
